import type { ParamMap } from '@angular/router';
import type { LocalStorageApiService } from '@nebalkon-workspace/browser-api';

/** Stable localStorage key for staff users list filters (web + mobile WebView). */
export const STAFF_USERS_LIST_FILTERS_STORAGE_KEY =
  'nebalkon.staffUsersListFilters';

export interface StaffUsersListFilters {
  excludeStaff: boolean;
  debtorsOnly: boolean;
  verifiedOnly: boolean;
  activeStorageOnly: boolean;
  activeOrdersOnly: boolean;
  blockedOnly: boolean;
  withoutStorageOnly: boolean;
  partnersOnly: boolean;
  personalRateOnly: boolean;
}

export const STAFF_USERS_LIST_FILTERS_DEFAULTS: StaffUsersListFilters = {
  excludeStaff: true,
  debtorsOnly: false,
  verifiedOnly: false,
  activeStorageOnly: false,
  activeOrdersOnly: false,
  blockedOnly: false,
  withoutStorageOnly: false,
  partnersOnly: false,
  personalRateOnly: false,
};

const FILTER_QUERY_PARAMS: ReadonlyArray<{
  queryKey: string;
  filterKey: keyof StaffUsersListFilters;
}> = [
  { queryKey: 'exclude_staff', filterKey: 'excludeStaff' },
  { queryKey: 'debtors_only', filterKey: 'debtorsOnly' },
  { queryKey: 'verified_only', filterKey: 'verifiedOnly' },
  { queryKey: 'active_storage_only', filterKey: 'activeStorageOnly' },
  { queryKey: 'active_orders_only', filterKey: 'activeOrdersOnly' },
  { queryKey: 'blocked_only', filterKey: 'blockedOnly' },
  { queryKey: 'without_storage_only', filterKey: 'withoutStorageOnly' },
  { queryKey: 'partners_only', filterKey: 'partnersOnly' },
  { queryKey: 'personal_rate_only', filterKey: 'personalRateOnly' },
];

const FILTER_KEYS = Object.keys(
  STAFF_USERS_LIST_FILTERS_DEFAULTS,
) as (keyof StaffUsersListFilters)[];

/** Parses `1` / `true` as enabled; any other explicit value as disabled. */
export function parseStaffUsersListFilterQueryValue(
  value: string | null | undefined,
): boolean {
  const normalized = value?.trim().toLowerCase() ?? '';
  return normalized === '1' || normalized === 'true';
}

function parseStoredStaffUsersListFilters(
  raw: string | null,
): Partial<StaffUsersListFilters> | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    const record = parsed as Record<string, unknown>;
    const partial: Partial<StaffUsersListFilters> = {};

    for (const key of FILTER_KEYS) {
      if (typeof record[key] === 'boolean') {
        partial[key] = record[key];
      }
    }

    return partial;
  } catch {
    return null;
  }
}

export function mergeStaffUsersListFilters(
  base: StaffUsersListFilters,
  partial: Partial<StaffUsersListFilters>,
): StaffUsersListFilters {
  return { ...base, ...partial };
}

/** Loads filters: defaults merged with persisted localStorage state. */
export function loadStaffUsersListFilters(
  storage: Pick<LocalStorageApiService, 'getItem'>,
): StaffUsersListFilters {
  const stored = parseStoredStaffUsersListFilters(
    storage.getItem(STAFF_USERS_LIST_FILTERS_STORAGE_KEY),
  );

  return mergeStaffUsersListFilters(
    STAFF_USERS_LIST_FILTERS_DEFAULTS,
    stored ?? {},
  );
}

export function saveStaffUsersListFilters(
  storage: Pick<LocalStorageApiService, 'setItem'>,
  filters: StaffUsersListFilters,
): void {
  storage.setItem(
    STAFF_USERS_LIST_FILTERS_STORAGE_KEY,
    JSON.stringify(filters),
  );
}

/** Applies only query params explicitly present in the URL (deep links). */
export function mergeStaffUsersListFiltersFromQueryParams(
  filters: StaffUsersListFilters,
  queryParamMap: ParamMap,
): StaffUsersListFilters {
  let result = filters;

  for (const { queryKey, filterKey } of FILTER_QUERY_PARAMS) {
    if (!queryParamMap.has(queryKey)) {
      continue;
    }

    result = {
      ...result,
      [filterKey]: parseStaffUsersListFilterQueryValue(
        queryParamMap.get(queryKey),
      ),
    };
  }

  return result;
}

/**
 * Resolves staff users list filters on page load:
 * defaults → localStorage → URL query params (URL wins) → persist result.
 */
export function resolveStaffUsersListFilters(
  queryParamMap: ParamMap,
  storage: Pick<LocalStorageApiService, 'getItem' | 'setItem'>,
): StaffUsersListFilters {
  const fromStorage = loadStaffUsersListFilters(storage);
  const resolved = mergeStaffUsersListFiltersFromQueryParams(
    fromStorage,
    queryParamMap,
  );

  saveStaffUsersListFilters(storage, resolved);

  return resolved;
}
